import React from 'react'
import { createProductGroup } from 'app-support/productgroup/state/actions'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'
import { useQueryClient } from '@cbgms/base/http/use-query'
import { productTreeCacheKey } from '../hooks/cache-keys'
import { useNotifier } from '@cbgms/base/notifier'
import { useGetProductTree } from '../hooks/useGetProductTree'
import { Dropdown, DropdownOption, TextField } from '@carsys/ui'
import { Box, Button, Card, CardContent, CardHeader, Divider, InputAdornment, Typography } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'

export function SubGroup() {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const notifier = useNotifier()
  const queryClient = useQueryClient()
  const history = useHistory()
  const { tree_uuid } = useParams<{ tree_uuid: string }>()
  const { maingroup_uuid } = useParams<{ maingroup_uuid: string }>()
  const { data: productGroup } = useGetProductTree(tree_uuid)
  const subGroups = productGroup?.Categories.find(caterory => caterory.UUID === maingroup_uuid)?.Categories || []
  const locale = productGroup?.Locale || undefined
  const [uuidSubGroup, setUuidSubGroup] = React.useState<string | undefined>('')
  const [newSubGroupName, setNewSubGroupName] = React.useState<string>('')

  const handleSubmit = async () => {
    const values = {
      Name: newSubGroupName,
      ParentUUID: maingroup_uuid,
      Locale: locale
    }

    try {
      await dispatch(createProductGroup(values))
      notifier.success(t('global.successfully_added'))
      queryClient.invalidateQueries(productTreeCacheKey(tree_uuid))
    } catch (e: any) {
      notifier.error(t('global.error'), e)
      throw new Error(e)
    }
  }

  const handleSelect = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setUuidSubGroup(event.target.value as string)
  }

  const goToGroup = () => {
    if (uuidSubGroup) {
      history.push(`/product-group/${tree_uuid}/main-group/${maingroup_uuid}/sub-group/${uuidSubGroup}`)
    }
  }

  return (
    <Card variant='outlined'>
      <CardHeader title={<Typography variant='h6'>{t('productgroup.tree_subgroup_title')}</Typography>} />
      <Divider />
      <CardContent sx={{ p: 2 }}>
        <Typography>{t('productgroup.tree_subgroup_info')}</Typography>
        <Typography sx={{ fontWeight: 700, mt: 2 }}>{t('productgroup.subTitle_Create')}</Typography>
        <TextField
          required
          fullWidth
          size='small'
          label={t('productgroup.name')}
          value={newSubGroupName}
          data-testid='new-subgroup-input'
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setNewSubGroupName(event.target.value)
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position='end'>
                <Button
                  data-testid='sub-group-add-btn'
                  disabled={!newSubGroupName}
                  variant='outlined'
                  size='small'
                  startIcon={<AddIcon />}
                  onClick={handleSubmit}
                >
                  {t('productgroup.tree_subgroup_add')}
                </Button>
              </InputAdornment>
            )
          }}
          sx={{ mt: 1 }}
        />
        <Divider sx={{ mt: 3, mb: 2 }}>{t('global.or')}</Divider>

        <Box sx={{ opacity: !subGroups || subGroups.length === 0 ? 0.4 : 1 }}>
          <Typography sx={{ fontWeight: 700, mb: 1 }}>{t('productgroup.subTitle_Edit')}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Dropdown
              label={t('productgroup.tree_subgroup_select')}
              value={uuidSubGroup}
              disabled={!subGroups || subGroups.length === 0}
              onChange={event => handleSelect(event)}
              sx={{ flex: '1 1 75%' }}
              data-testid='sub-group-selector-dropdown'
            >
              {subGroups.map((group, index) => {
                return (
                  <DropdownOption key={`subGroupItem-select-${index}`} value={group.UUID}>
                    {group.Name}
                  </DropdownOption>
                )
              })}
            </Dropdown>
            <Button
              data-testid='edit-btn'
              variant='outlined'
              disabled={!subGroups}
              onClick={goToGroup}
              startIcon={<EditIcon />}
              sx={{ ml: 2 }}
            >
              {t('productgroup.tree_subgroup_edit')}
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  )
}

export default SubGroup
